import { MessageMedium } from 'modules/domains/campaign';
import { ExperimentType } from 'modules/graphql';
import {
  FieldNames,
  GqlUpsertTemplateInvalidContentFailureTypes,
  ValidationError,
} from 'modules/templateEditor/common';
import {
  emailFromNameSchema,
  emailPreheaderTextSchema,
  emailReplyToSchema,
  emailSendersEmailSchema,
  emailSubjectLineSchema,
  pushMessageSchema,
  pushTitleSchema,
  smsImageUrlSchema,
  smsMessageSchema,
  webPushBodySchema,
  webPushClickLocationSchema,
  webPushIconSchema,
  webPushTitleOrBodyRefinement,
  webPushTitleSchema,
} from 'modules/templateEditor/integrations/zod';
import { TFunction } from 'react-i18next';
import { parseISOZonedDate } from 'utils/date';
import { z } from 'zod';
import { sendTimeSchema } from '../schemas';
import {
  MutatedExperimentVariant,
  PickEmailContentDefinitionProperties,
  PickPushContentDefinitionProperties,
  PickSMSContentDefinitionProperties,
  PickWebPushContentDefinitionProperties,
} from '../takeover/types';
import { VariantSendTime } from '../types';
import {
  VariantComboFormFieldKey,
  VariantFormField,
  VariantFormFieldKey,
  VariantFormFieldSchemaReturnType,
  VariantFormFieldType,
  VariantFormFieldValueReturnType,
} from '../types/variantCard';
import { getOnlyDateFormattedFromISOZonedDate, getOnlyTimeFormattedFromISOZonedDate } from './date';

enum VariantFormFieldMode {
  Schema,
  Value,
}

type VariantFormFieldSchemaArgs = {
  t: TFunction;
  type: ExperimentType;
  messageMedium: MessageMedium;
};

type VariantFormFieldValueArgs = {
  variant: MutatedExperimentVariant;
  type: ExperimentType;
  messageMedium: MessageMedium;
};

type SendTimeVariantFormFieldValueArgs = {
  variant: MutatedExperimentVariant;
  controlStartTime: string;
  sendTimeOfAllVariants: VariantSendTime[];
  sendTimeStartTimeZone: string;
};

type VariantFormFieldArgs = {
  type: ExperimentType;
  mode: VariantFormFieldMode;
  t: TFunction;
  variant?: MutatedExperimentVariant;
};

type VariantFormFieldReturnType =
  | VariantFormFieldSchemaReturnType
  | VariantFormFieldValueReturnType;

const SEND_TIME_COMBO_FORM_FIELD: VariantFormField[] = [
  {
    key: VariantComboFormFieldKey.SEND_TIME,
    type: VariantFormFieldType.COMBO,
    fields: [
      {
        key: VariantFormFieldKey.SEND_TIME_DATE,
        type: VariantFormFieldType.TEXT,
        label: '',
      },
      {
        key: VariantFormFieldKey.SEND_TIME_TIME,
        type: VariantFormFieldType.TEXT,
        label: '',
      },
    ],
  },
];

/**
 * In order to have context about the other variant's send time
 * and the experiment's time zone we pass these attributes as
 * additional attributes to the send time combo field.
 * Even though we won't be displaying all these values in the UI
 * we will be using them to do form field validations.
 */
export const getDefaultSendTimeFieldValues = ({
  variant,
  controlStartTime,
  sendTimeOfAllVariants,
  sendTimeStartTimeZone,
}: SendTimeVariantFormFieldValueArgs): VariantFormFieldValueReturnType => ({
  [VariantFormFieldKey.SEND_TIME_DATE]: getOnlyDateFormattedFromISOZonedDate(
    variant?.sendTime || null,
  ),
  [VariantFormFieldKey.SEND_TIME_TIME]: getOnlyTimeFormattedFromISOZonedDate(
    variant?.sendTime || null,
  ),
  [VariantFormFieldKey.SEND_TIME_VALIDATION_META_INFO]: {
    variantId: variant.id,
    isControl: variant.control,
    originalSendTimeZone: parseISOZonedDate(controlStartTime).tz(),
    experimentSendTimeZone: sendTimeStartTimeZone,
    allVariantSendTimes: sendTimeOfAllVariants,
  },
});

const defaultFormFieldSchema: VariantFormFieldSchemaReturnType = {
  formFieldSchema: [],
  formFieldZodSchema: z.object({}),
};

const defaultFormFieldValue: VariantFormFieldValueReturnType = {};

const getFormField = (
  mode: VariantFormFieldMode,
  schema: VariantFormFieldSchemaReturnType,
  value: VariantFormFieldValueReturnType,
) => (mode === VariantFormFieldMode.Schema ? schema : value);

/**
 *
 * To support any future experiment types. Just add a new case statement.
 * And, set the form field schema and the zod schema for the field.
 * Also, set the value for the form field from the variant object.
 *
 */

const getEmailVariantFormField = ({
  t,
  mode,
  variant,
  type,
}: VariantFormFieldArgs): VariantFormFieldReturnType => {
  const emailContent = variant?.content as PickEmailContentDefinitionProperties;
  let schema = defaultFormFieldSchema;
  let value = defaultFormFieldValue;

  switch (type) {
    case ExperimentType.SubjectLine: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.EMAIL_SUBJECT_LINE,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.subjectLine'),
            isFocused: true,
          },
        ],
        formFieldZodSchema: z.object({
          [VariantFormFieldKey.EMAIL_SUBJECT_LINE]: emailSubjectLineSchema,
        }),
      };
      value = {
        [VariantFormFieldKey.EMAIL_SUBJECT_LINE]: emailContent?.subject,
      };
      break;
    }

    case ExperimentType.PreheaderText: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.EMAIL_PRE_HEADER,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.preheaderText'),
            isFocused: true,
          },
        ],
        formFieldZodSchema: z.object({
          [VariantFormFieldKey.EMAIL_PRE_HEADER]: emailPreheaderTextSchema,
        }),
      };
      value = {
        [VariantFormFieldKey.EMAIL_PRE_HEADER]: emailContent?.preheaderText,
      };
      break;
    }

    case ExperimentType.FromNameAndSender: {
      schema = {
        formFieldSchema: [
          {
            key: VariantComboFormFieldKey.EMAIL_SENDERS,
            type: VariantFormFieldType.COMBO,
            fields: [
              {
                key: VariantFormFieldKey.EMAIL_FROM_NAME,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.fromName'),
                isFocused: true,
              },
              {
                key: VariantFormFieldKey.EMAIL_SENDERS_EMAIL,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.senderEmail'),
              },
              {
                key: VariantFormFieldKey.EMAIL_REPLY_TO_EMAIL,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.replyTo'),
              },
            ],
          },
        ],
        formFieldZodSchema: z.object({
          [VariantFormFieldKey.EMAIL_FROM_NAME]: emailFromNameSchema,
          [VariantFormFieldKey.EMAIL_SENDERS_EMAIL]: emailSendersEmailSchema,
          [VariantFormFieldKey.EMAIL_REPLY_TO_EMAIL]: emailReplyToSchema,
        }),
      };
      value = {
        [VariantFormFieldKey.EMAIL_FROM_NAME]: emailContent?.fromName,
        [VariantFormFieldKey.EMAIL_SENDERS_EMAIL]: emailContent?.fromEmail,
        [VariantFormFieldKey.EMAIL_REPLY_TO_EMAIL]: emailContent?.replyTo,
      };
      break;
    }

    case ExperimentType.EmailBody: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.EMAIL_BODY,
            type: VariantFormFieldType.NON_EDITABLE,
            label: t('variantCard.fieldLabel.emailBody'),
          },
        ],
        formFieldZodSchema: z.object({}),
      };
      value = {};
      break;
    }

    case ExperimentType.WholeEmail: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.EMAIL_SUBJECT_LINE,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.subjectLine'),
            isFocused: true,
          },
          {
            key: VariantFormFieldKey.EMAIL_PRE_HEADER,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.preheaderText'),
          },
          {
            key: VariantComboFormFieldKey.EMAIL_SENDERS,
            type: VariantFormFieldType.COMBO,
            fields: [
              {
                key: VariantFormFieldKey.EMAIL_FROM_NAME,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.fromName'),
              },
              {
                key: VariantFormFieldKey.EMAIL_SENDERS_EMAIL,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.senderEmail'),
              },
              {
                key: VariantFormFieldKey.EMAIL_REPLY_TO_EMAIL,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.replyTo'),
              },
            ],
          },
        ],
        formFieldZodSchema: z.object({
          [VariantFormFieldKey.EMAIL_SUBJECT_LINE]: emailSubjectLineSchema,
          [VariantFormFieldKey.EMAIL_PRE_HEADER]: emailPreheaderTextSchema,
          [VariantFormFieldKey.EMAIL_FROM_NAME]: emailFromNameSchema,
          [VariantFormFieldKey.EMAIL_SENDERS_EMAIL]: emailSendersEmailSchema,
          [VariantFormFieldKey.EMAIL_REPLY_TO_EMAIL]: emailReplyToSchema,
        }),
      };
      value = {
        [VariantFormFieldKey.EMAIL_SUBJECT_LINE]: emailContent?.subject,
        [VariantFormFieldKey.EMAIL_PRE_HEADER]: emailContent?.preheaderText,
        [VariantFormFieldKey.EMAIL_FROM_NAME]: emailContent?.fromName,
        [VariantFormFieldKey.EMAIL_SENDERS_EMAIL]: emailContent?.fromEmail,
        [VariantFormFieldKey.EMAIL_REPLY_TO_EMAIL]: emailContent?.replyTo,
      };
      break;
    }

    case ExperimentType.SendTime: {
      schema = {
        formFieldSchema: SEND_TIME_COMBO_FORM_FIELD,
        formFieldZodSchema: sendTimeSchema,
      };
      value = {};
      break;
    }

    case ExperimentType.SendTimeOptimization: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.STO,
            type: VariantFormFieldType.STO,
          },
        ],
        formFieldZodSchema: z.object({}),
      };
      value = {};
      break;
    }

    default:
  }

  return getFormField(mode, schema, value);
};

const getInAppVariantFormField = ({
  t,
  mode,
  type,
}: VariantFormFieldArgs): VariantFormFieldReturnType => {
  let schema = defaultFormFieldSchema;
  let value = defaultFormFieldValue;

  switch (type) {
    case ExperimentType.WholeEmail: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.EVERYTHING,
            type: VariantFormFieldType.NON_EDITABLE,
            label: t('variantCard.fieldLabel.everything'),
          },
        ],
        formFieldZodSchema: z.object({}),
      };
      value = {};
      break;
    }

    default:
  }

  return getFormField(mode, schema, value);
};

const getPushVariantFormField = ({
  t,
  mode,
  variant,
  type,
}: VariantFormFieldArgs): VariantFormFieldReturnType => {
  const pushContent = variant?.content as PickPushContentDefinitionProperties;
  let schema = defaultFormFieldSchema;
  let value = defaultFormFieldValue;

  switch (type) {
    case ExperimentType.WholeEmail: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.PUSH_TITLE,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.title'),
            isFocused: true,
          },
          {
            key: VariantFormFieldKey.PUSH_MESSAGE,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.message'),
          },
        ],
        formFieldZodSchema: z.object({
          [VariantFormFieldKey.PUSH_TITLE]: pushTitleSchema,
          [VariantFormFieldKey.PUSH_MESSAGE]: pushMessageSchema,
        }),
      };
      value = {
        [VariantFormFieldKey.PUSH_TITLE]: pushContent?.title,
        [VariantFormFieldKey.PUSH_MESSAGE]: pushContent?.message,
      };
      break;
    }

    case ExperimentType.SendTime: {
      schema = {
        formFieldSchema: SEND_TIME_COMBO_FORM_FIELD,
        formFieldZodSchema: sendTimeSchema,
      };
      value = {};
      break;
    }

    default:
  }

  return getFormField(mode, schema, value);
};

const getSMSVariantFormField = ({
  t,
  mode,
  variant,
  type,
}: VariantFormFieldArgs): VariantFormFieldReturnType => {
  const smsContent = variant?.content as PickSMSContentDefinitionProperties;
  let schema = defaultFormFieldSchema;
  let value = defaultFormFieldValue;

  switch (type) {
    case ExperimentType.WholeEmail: {
      schema = {
        formFieldSchema: [
          {
            key: VariantFormFieldKey.SMS_MESSAGE,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.message'),
            isFocused: true,
          },
          {
            key: VariantFormFieldKey.SMS_IMAGE_URL,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.imageUrl'),
          },
        ],
        formFieldZodSchema: z.object({
          [VariantFormFieldKey.SMS_MESSAGE]: smsMessageSchema,
          [VariantFormFieldKey.SMS_IMAGE_URL]: smsImageUrlSchema,
        }),
      };
      value = {
        [VariantFormFieldKey.SMS_MESSAGE]: smsContent?.message,
        [VariantFormFieldKey.SMS_IMAGE_URL]: smsContent?.imageUrl,
      };
      break;
    }

    case ExperimentType.SendTime: {
      schema = {
        formFieldSchema: SEND_TIME_COMBO_FORM_FIELD,
        formFieldZodSchema: sendTimeSchema,
      };
      value = {};
      break;
    }

    default:
  }

  return getFormField(mode, schema, value);
};

const getWebPushVariantFormField = ({
  t,
  mode,
  variant,
  type,
}: VariantFormFieldArgs): VariantFormFieldReturnType => {
  const webPushContent = variant?.content as PickWebPushContentDefinitionProperties;
  let schema = defaultFormFieldSchema;
  let value = defaultFormFieldValue;

  switch (type) {
    case ExperimentType.WholeEmail: {
      schema = {
        formFieldSchema: [
          {
            key: VariantComboFormFieldKey.WEB_PUSH_TITLE_BODY,
            type: VariantFormFieldType.COMBO,
            fields: [
              {
                key: VariantFormFieldKey.WEB_PUSH_TITLE,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.title'),
                isFocused: true,
              },
              {
                key: VariantFormFieldKey.WEB_PUSH_BODY,
                type: VariantFormFieldType.TEXT,
                label: t('variantCard.fieldLabel.body'),
              },
            ],
          },
          {
            key: VariantFormFieldKey.WEB_PUSH_ICON,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.icon'),
          },
          {
            key: VariantFormFieldKey.WEB_PUSH_CLICK_LOCATION,
            type: VariantFormFieldType.TEXT,
            label: t('variantCard.fieldLabel.clickLocation'),
          },
        ],
        formFieldZodSchema: z
          .object({
            [VariantFormFieldKey.WEB_PUSH_TITLE]: webPushTitleSchema,
            [VariantFormFieldKey.WEB_PUSH_BODY]: webPushBodySchema,
            [VariantFormFieldKey.WEB_PUSH_ICON]: webPushIconSchema,
            [VariantFormFieldKey.WEB_PUSH_CLICK_LOCATION]: webPushClickLocationSchema,
          })
          .refine(
            (data) =>
              webPushTitleOrBodyRefinement(
                data,
                VariantFormFieldKey.WEB_PUSH_TITLE,
                VariantFormFieldKey.WEB_PUSH_BODY,
              ),
            {
              message: 'templates.webPush.requiredTitleOrBody',
              path: [VariantFormFieldKey.WEB_PUSH_TITLE],
            },
          )
          .refine(
            (data) =>
              webPushTitleOrBodyRefinement(
                data,
                VariantFormFieldKey.WEB_PUSH_TITLE,
                VariantFormFieldKey.WEB_PUSH_BODY,
              ),
            {
              message: 'templates.webPush.requiredTitleOrBody',
              path: [VariantFormFieldKey.WEB_PUSH_BODY],
            },
          ),
      };
      value = {
        [VariantFormFieldKey.WEB_PUSH_TITLE]: webPushContent?.title,
        [VariantFormFieldKey.WEB_PUSH_BODY]: webPushContent?.message,
        [VariantFormFieldKey.WEB_PUSH_ICON]: webPushContent?.icon,
        [VariantFormFieldKey.WEB_PUSH_CLICK_LOCATION]: webPushContent?.clickAction,
      };
      break;
    }

    default:
  }

  return getFormField(mode, schema, value);
};

/**
 * A little bit of function overloading magic out here
 * If the mode is schema we will return the formFieldSchema and formFieldZodSchema
 * If the mode is value we will return the variant content values
 */
function getVariantFormField(
  variantFormFieldsArgs: VariantFormFieldSchemaArgs,
  mode: VariantFormFieldMode.Schema,
): VariantFormFieldSchemaReturnType;
function getVariantFormField(
  variantFormFieldsArgs: VariantFormFieldValueArgs,
  mode: VariantFormFieldMode.Value,
): VariantFormFieldValueReturnType;
function getVariantFormField(
  variantFormFieldsArgs: VariantFormFieldSchemaArgs | VariantFormFieldValueArgs,
  mode: VariantFormFieldMode,
): VariantFormFieldReturnType {
  const { messageMedium } = variantFormFieldsArgs;
  // Defaulting the TFunction when the mode is 'Value'
  const t =
    mode === VariantFormFieldMode.Schema
      ? (variantFormFieldsArgs as VariantFormFieldSchemaArgs).t
      : (key: string) => key;
  const variantFormFieldArgs = {
    ...variantFormFieldsArgs,
    mode,
    t,
  };

  switch (messageMedium) {
    case MessageMedium.Email:
      return getEmailVariantFormField(variantFormFieldArgs);

    case MessageMedium.InApp:
      return getInAppVariantFormField(variantFormFieldArgs);

    case MessageMedium.Push:
      return getPushVariantFormField(variantFormFieldArgs);

    case MessageMedium.SMS:
      return getSMSVariantFormField(variantFormFieldArgs);

    case MessageMedium.WebPush:
      return getWebPushVariantFormField(variantFormFieldArgs);

    // Handle future mediums out here

    default:
      return getFormField(mode, defaultFormFieldSchema, defaultFormFieldValue);
  }
}

export const getVariantFormFieldSchema = (
  varianFormFieldsArgs: VariantFormFieldSchemaArgs,
): VariantFormFieldSchemaReturnType =>
  getVariantFormField(varianFormFieldsArgs, VariantFormFieldMode.Schema);

export const getVariantFormFieldValue = (
  varianFormFieldsArgs: VariantFormFieldValueArgs,
): VariantFormFieldValueReturnType =>
  getVariantFormField(varianFormFieldsArgs, VariantFormFieldMode.Value);

export const getFieldTemplateErrors = (
  templateErrors: ValidationError[],
  fieldName: FieldNames,
) => {
  /**
   * We do some of the template validation in the frontend
   * So, we specifically remove those error types from our
   * backend validation. And, let the RHF to handle them.
   */
  let filteredTemplateErrors = [];

  if (fieldName === FieldNames.SUBJECT) {
    filteredTemplateErrors = templateErrors.map((templateError) =>
      templateError.__typename === GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT
        ? { ...templateError, errorMessage: 'Invalid content' }
        : templateError,
    );
  } else if (fieldName === FieldNames.MISSING_MESSAGE_OR_SILENT_PUSH) {
    filteredTemplateErrors = templateErrors.filter(
      (templateError) =>
        ![GqlUpsertTemplateInvalidContentFailureTypes.MISSING_MESSAGE_OR_SILENT_PUSH].includes(
          templateError.__typename,
        ),
    );
  } else {
    filteredTemplateErrors = templateErrors;
  }

  return filteredTemplateErrors.find((templateError) => templateError.fieldName === fieldName)
    ?.errorMessage;
};
